import toast from 'react-hot-toast';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useGetCapacityComponentsFlowsQuery,
  useGetCapacityComponentFlowHistoryQuery,
  useGetFlowSliceHistoryMutation,
  useFavoriteItemMutation,
  useUnfavoriteItemMutation,
  useGetNoticeComponentQuery,
  useGetScenariosQuery,
  useGetPriceComponentHistoryQuery,
} from '../services/edgeApi';
import { toIsoDate } from '../utils/stringUtils';
import { useDispatch, useSelector } from 'react-redux';
import { utils } from 'xlsx';
import * as filtersSlice from '../filters/filtersSlice';
import { useMap, useMapEvents } from 'react-leaflet';
import { mapContext } from '../map/EdgeMap';
import { WebGLLayer } from '../map/webglLayer';
import { Vector as VectorSource } from 'ol/source.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { toDate } from '../utils/dateUtils';
// import proccess from 'process';

export const useDelete = () => {
  const deleteTrigger = async (deleteFunction, _name) => {
    // if (!window.confirm(`Are you sure you want to delete ${name}?`)){
    // should make this useConfirm and make more of a customizable message
    if (!window.confirm('Are you sure?')) {
      return null;
    }

    try {
      await deleteFunction();
      // TODO: might need to pass in an onSuccess function
      // dispatch(selectedItemsActions.setState({edgeStartNode: [], edgeEndNode: [], edge: ['new']}))
      toast.success('We did it!');
    } catch {
      toast.error('Error, please try again');
    }
  };

  return deleteTrigger;
};

// can set this up as a hook to call functions, or can set it up as a simple function that returns true or false, basically all a window.confirm already does
// export const useConfirm = (message, onConfirm, onAbort) => {
//   const confirm = () => {
//     if(window.confirm(message))
//       onConfirm();
//     else
//       onAbort();
//   }
//   return confirm
// }

export const useConfirm = () => {
  const confirm = (message) =>
    // just return true or false
    window.confirm(message);

  return confirm;
};

export function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

/**
 * @template {unknown} T
 * @param {T} initialValue
 * @param {number} delay
 * @returns {import('../types/types').State<T>}
 */
export const useDebouncedState = (initialValue, delay) => {
  const [value, _setValue] = useState(initialValue);
  let timeout = useRef(null);

  const setValue = useCallback(
    (setter) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        _setValue(setter);
      }, delay);
    },
    [delay],
  );

  return [value, setValue];
};

/**
 * @template {(...args: any) => void} T
 * @param {T} func
 * @param {number} delay
 * @param {React.DependencyList} listeners
 * @returns {T} A function that when called will execute the original function after the delay. If called again before the delay, the timer will reset.
 * @warning This hook is unsuitable for functions that are triggered by inputs, in these cases the `DebouncedInput` component should be used.
 * If you can't avoid using this hook for an input callback and you're expecting the input value to be modified by the callback, make sure to pass the value
 * as `defaultValue` to the input component instead of `value`. If you do so, you will be able to use the input but might still experience issues if the value
 * is modified by a different source.
 */
export const useDebouncedCallback = (func, delay = 400, listeners = []) => {
  const timeoutId = useRef(null);
  return useCallback((...args) => {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      func(...args);
    }, delay);
  }, listeners);
};

/**
 *
 * @param {{
 *  gasDate: string;
 *  nomCycle: string;
 *  itemsPerPage?: number;
 *  capacityType?: import('../types').CapacityType;
 *  searchText?: string;
 *  tsps?: number[] | string[];
 * }} options
 * @param {boolean} allMappedSegments
 * @returns {{
 *  flows: import('../types/api').Flow[];
 *  flowsLoading: boolean;
 *  flowsFetching: boolean;
 *  flowsLoadError: boolean;
 *  fetchNextPage: () => void;
 *  reset: () => void;
 * }}
 */
export const useCapacityComponentsFlows = (
  {
    gasDate,
    nomCycle,
    itemsPerPage = 30,
    capacityType = null,
    searchText = null,
    tsps,
  } = {},
  allMappedSegments = false,
) => {
  /** @type {import('../types/types').State<boolean>} */
  const [shouldFetch, setShouldFetch] = useState(true);

  /** @type {import('../types/types').State<boolean>} */
  const [exhausted, setExhausted] = useState(false);

  /** @type {import('../types/types').State<import('../types/api').Flow[]>} */
  const [flows, setFlows] = useState([]);

  /** @type {import('../types/types').State<number>} */
  const [cursor, setCursor] = useState(0);

  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(
    searchText,
    500,
  );

  useEffect(() => {
    setDebouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setFlows([]);
  }, [
    debouncedSearch,
    gasDate,
    nomCycle,
    capacityType,
    itemsPerPage,
    tsps,
    allMappedSegments,
  ]);

  const allLoadMode = allMappedSegments === true;

  const filters = allLoadMode
    ? {
        gasDate,
        nomCycle,
        defaultFilter: true,
        tsps,
      }
    : {
        gasDate,
        nomCycle,
        limit: itemsPerPage,
        offset: cursor,
        capacityType,
        searchText,
        tsps,
      };

  const { data, isLoading, isFetching, isError } =
    useGetCapacityComponentsFlowsQuery(filters, {
      skip: !shouldFetch || exhausted,
    });

  if (shouldFetch && isFetching === false && exhausted === false) {
    const pageItems = Array.isArray(data) ? data : Object.values(data || {});

    if (allLoadMode) {
      // In map all-load mode, fetch once and disable backend infinite scroll.
      setFlows(pageItems);
      setShouldFetch(false);
      setExhausted(true);
    } else {
      setFlows((prevFlows) => [...prevFlows, ...pageItems]);
      setShouldFetch(false);

      // Backend pagination is applied to CapacityComponents before flattening to slices,
      // so cursor must advance by requested page size, not returned row count.
      if (pageItems.length > 0) {
        setCursor((prevCursor) => prevCursor + itemsPerPage);
      }

      if (pageItems.length === 0) {
        setExhausted(true);
      }
    }
  }

  const fetchNextPage = useCallback(() => {
    if (shouldFetch || exhausted) return;
    setShouldFetch(true);
  }, [setShouldFetch, shouldFetch, exhausted]);

  const reset = () => {
    setShouldFetch(true);
    setExhausted(false);
    setFlows([]);
    setCursor(0);
  };

  return {
    flows,
    flowsLoading: isLoading,
    flowsFetching: isFetching,
    flowsLoadError: isError,
    fetchNextPage,
    reset,
  };
};

/**
 *
 * @param {import('../types/api').Flow[]} flows The flows array
 * @returns {Date | null} The lowest gas date in the flows array
 */
const getLowestGasDate = (flows) => {
  if (flows.length === 0) {
    return null;
  }

  const dates = flows.map((flow) => {
    if (flow?.start_gas_date) {
      return toDate(flow.start_gas_date);
    } else {
      return toDate(flow.gas_date);
    }
  });
  return dates.sort((dateA, dateB) => {
    return dateA.getTime() - dateB.getTime();
  })[0];
};

const getPreviousDate = (date) => {
  const previous = new Date(date);
  previous.setDate(previous.getDate() - 1);
  return previous;
};

export const useCapacityComponentsHistory = ({
  componentId,
  itemsPerPage = 30,
}) => {
  const [shouldFetch, setShouldFetch] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [capacityComponent, setCapacityComponent] = useState(null);
  const [flows, setFlows] = useState([]);
  const [cursor, setCursor] = useState(toIsoDate());

  const { data, isLoading, isFetching, isError, error } =
    useGetCapacityComponentFlowHistoryQuery(
      { componentId, limit: itemsPerPage, offset: cursor },
      { skip: !componentId || !shouldFetch || exhausted },
    );

  if (isError) {
    setShouldFetch(false);
  } else if (
    shouldFetch &&
    isFetching === false &&
    exhausted === false &&
    data
  ) {
    // TODO: make this dynamic when we add sources
    // This spread is necessary because the array is read only
    const pipelineFlows = [...data.flows];

    setCapacityComponent(data);
    setFlows((prevFlows) => [...prevFlows, ...pipelineFlows]);
    setShouldFetch(false);

    if (pipelineFlows.length > 0) {
      const nextCursor = getLowestGasDate(pipelineFlows);
      setCursor(toIsoDate(nextCursor));
    }
    if (pipelineFlows.length === 0) {
      setExhausted(true);
    }
  }

  const fetchNextPage = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  return {
    capacityComponent,
    flows,
    flowsLoading: isLoading,
    flowsFetching: isFetching,
    flowsLoadError: isError,
    error,
    fetchNextPage,
    exhausted,
  };
};

/**
 * Hook for fetching flow history using a FlowSliceKey via POST request.
 * This uses a mutation under the hood since we need to send complex data in the body.
 *
 * @param {{
 *  flowSliceKey: import('../types').FlowSliceKey | null,
 *  itemsPerPage: number
 * }} params
 * @returns
 */
export const useFlowSliceHistory = ({ flowSliceKey, itemsPerPage = 30 }) => {
  const [exhausted, setExhausted] = useState(false);
  const [capacityComponent, setCapacityComponent] = useState(null);
  /** @type {import('../types').State<import('../types/api/Flow').FlowSource[]>} */
  const [flows, setFlows] = useState([]);
  const [cursor, setCursor] = useState(toIsoDate());
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const [_fetchHistory, { isLoading, isError, error }] =
    useGetFlowSliceHistoryMutation();

  // Reset state when flowSliceKey changes
  useEffect(() => {
    setFlows([]);
    setCapacityComponent(null);
    setExhausted(false);
    setCursor(toIsoDate());
    setInitialFetchDone(false);
  }, [flowSliceKey]);

  const fetchHistory = useCallback(
    (params) => {
      if (!params.key) return null;
      if (
        typeof params.key === 'object' &&
        Boolean(params.key.component_id) === false
      )
        return null;
      return _fetchHistory(params);
    },
    [_fetchHistory],
  );

  // Fetch initial data when flowSliceKey is set
  useEffect(() => {
    if (flowSliceKey && !initialFetchDone && !exhausted) {
      fetchHistory({
        key: flowSliceKey,
        limit: itemsPerPage,
        offset: cursor,
      })
        ?.unwrap()
        .then((data) => {
          // TODO: make this dynamic when we add sources
          const pipelineFlows = [...(data.flows || [])];

          setCapacityComponent(data);
          setFlows(pipelineFlows);
          setInitialFetchDone(true);

          if (pipelineFlows.length > 0) {
            const lowestDate = getLowestGasDate(pipelineFlows);
            const nextCursor = getPreviousDate(lowestDate);
            setCursor(toIsoDate(nextCursor));
          }
          if (pipelineFlows.length < itemsPerPage) {
            setExhausted(true);
          }
        })
        .catch((err) => {
          console.error('Error fetching flow slice history:', err);
          setInitialFetchDone(true);
        });
    }
  }, [
    flowSliceKey,
    initialFetchDone,
    exhausted,
    cursor,
    error,
    itemsPerPage,
    fetchHistory,
  ]);

  const fetchNextPage = useCallback(() => {
    if (!flowSliceKey || exhausted || isLoading) return;

    fetchHistory({
      key: flowSliceKey,
      limit: itemsPerPage,
      offset: cursor,
    })
      ?.unwrap()
      .then((data) => {
        const pipelineFlows = [...(data.flows || [])];

        setCapacityComponent(data);
        setFlows((prevFlows) => [...prevFlows, ...pipelineFlows]);

        if (pipelineFlows.length > 0) {
          const lowestDate = getLowestGasDate(pipelineFlows);
          const nextCursor = getPreviousDate(lowestDate);
          setCursor(toIsoDate(nextCursor));
        }
        if (pipelineFlows.length < itemsPerPage) {
          setExhausted(true);
        }
      })
      .catch((err) => {
        console.error('Error fetching next page:', err);
      });
  }, [flowSliceKey, exhausted, isLoading, cursor, itemsPerPage, fetchHistory]);

  return {
    capacityComponent,
    flows,
    flowsLoading: isLoading,
    flowsFetching: isLoading,
    flowsLoadError: isError,
    error,
    fetchNextPage,
    exhausted,
  };
};

/**
 *
 * @param {{
 *  componentId: string | number,
 *  itemsPerPage: number,
 *  priceSource: import('../scenarios/scenarioSlice').PriceDataProvider
 * }} params
 * @returns
 */
export const usePriceComponentHistory = ({
  componentId,
  itemsPerPage = 100,
  priceSource = 'PLATTS_DAILY',
}) => {
  const [shouldFetch, setShouldFetch] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [priceComponent, setPriceComponent] = useState(null);
  const [prices, setPrices] = useState([]);
  const [cursor, setCursor] = useState(toIsoDate(new Date()));

  const { data, isLoading, isFetching, isError, error } =
    useGetPriceComponentHistoryQuery(
      { componentId, pageSize: itemsPerPage, cursor, priceSource },
      { skip: !shouldFetch || exhausted },
    );

  if (isError) {
    setShouldFetch(false);
    setExhausted(true);
  } else if (shouldFetch && isFetching === false && exhausted === false) {
    setPriceComponent(data.component);
    const history = Object.values(
      data.history.reduce((acc, record) => {
        if (!acc[record.assess_date]) {
          acc[record.assess_date] = {
            gas_date: new Date(record.assess_date).toLocaleDateString(),
          };
        }
        acc[record.assess_date][record.bate] = record.value;
        return acc;
      }, {}),
    );
    setPrices((prevFlows) => [...prevFlows, ...history]);
    setShouldFetch(false);

    if (history.length > 0) {
      setCursor(data.cursor);
    } else {
      setExhausted(true);
    }
  }

  const fetchNextPage = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  const reset = useCallback(() => {
    setShouldFetch(true);
    setExhausted(false);
    setPrices([]);
    setCursor(toIsoDate(new Date()));
  }, [setShouldFetch, setExhausted, setPrices, setCursor]);

  useEffect(() => {
    reset();
  }, [componentId, priceSource]);

  return {
    priceComponent,
    prices,
    isLoading,
    isFetching,
    isError,
    error,
    exhausted,
    fetchNextPage,
    reset,
  };
};

export const useNoticeComponentsHistory = ({
  componentId,
  itemsPerPage = 30,
}) => {
  const [shouldFetch, setShouldFetch] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [noticeComponent, setNoticeComponent] = useState(null);
  const [flows, setFlows] = useState([]);
  const [cursor, setCursor] = useState(toIsoDate(new Date()));
  const { data, isLoading, isFetching, isError } = useGetNoticeComponentQuery(
    { componentId, limit: itemsPerPage, offset: cursor },
    { skip: !shouldFetch || exhausted },
  );
  if (isError) {
    setShouldFetch(false);
  } else if (shouldFetch && isFetching === false && exhausted === false) {
    const pipelineFlows = [...(data.notice_records ?? [])];
    setNoticeComponent(data);
    setFlows((prevFlows) => [...prevFlows, ...pipelineFlows]);
    setShouldFetch(false);
    if (pipelineFlows.length > 0) {
      const nextCursor = getLowestGasDate(pipelineFlows);
      setCursor(toIsoDate(nextCursor));
    }
    if (pipelineFlows.length === 0) {
      setExhausted(true);
    }
  }
  const fetchNextPage = () => {
    setShouldFetch(true);
  };
  return {
    noticeComponent,
    flows,
    flowsLoading: isLoading,
    flowsFetching: isFetching,
    flowsLoadError: isError,
    fetchNextPage,
  };
};

/**
 *
 * @param {{ all?: boolean, flowDate?: Date | null, search?: string, pageSize?: number, showSystem?: boolean}} params
 * @returns {{
 *  scenarios: import('../scenarios/scenarioSlice').Scenario[],
 *  isError: boolean,
 *  isLoading: boolean,
 *  isFetching: boolean,
 *  exhausted: boolean,
 *  fetchNextPage: () => void,
 *  reset: () => void,
 * }}
 */
export const useScenarios = ({
  all = false,
  flowDate = null,
  search,
  showSystem = false,
  pageSize = 30,
}) => {
  const [shouldFetch, setShouldFetch] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [exhausted, setExhausted] = useState(false);
  const [cursor, setCursor] = useState(new Date().toISOString());
  const { data, isError, isFetching, isLoading, isSuccess } =
    useGetScenariosQuery(
      {
        all,
        search,
        limit: pageSize,
        offset: cursor,
        flow_date: flowDate,
        system: showSystem ? null : 0,
      },
      { skip: !shouldFetch || exhausted },
    );

  useUpdateEffect(() => reset(), [search, all, pageSize, flowDate, showSystem]);

  if (shouldFetch) {
    if (isError) {
      setShouldFetch(false);
    } else if (!isFetching && isSuccess && !exhausted) {
      const response = data || [];
      setScenarios((s) => [...s, ...response]);
      setShouldFetch(false);

      if (response.length > 0) {
        const nextCursor = response[response.length - 1].created_at;
        setCursor(nextCursor);
      }
      if (response.length === 0) {
        setExhausted(true);
      }
    }
  }

  const fetchNextPage = () => {
    if (exhausted) return;
    setShouldFetch(true);
  };

  const reset = () => {
    setCursor(new Date().toISOString());
    setScenarios([]);
    setExhausted(false);
    setShouldFetch(true);
  };

  return {
    scenarios,
    isError,
    isLoading,
    isFetching,
    exhausted,
    fetchNextPage,
    reset,
  };
};

export const useToggleFavorite = (itemType, initial) => {
  const [favoriteState, setFavoriteState] = useState({});

  const [favoriteMutation, favoriteMutationData] = useFavoriteItemMutation();
  const [unfavoriteMutation, unfavoriteMutationData] =
    useUnfavoriteItemMutation();

  const getFavorite = useCallback(
    (id) => {
      if (favoriteState[id] !== undefined) {
        return favoriteState[id];
      }

      if (initial?.[id] !== undefined) {
        return initial?.[id];
      }

      return false;
    },
    [favoriteState, initial],
  );

  const toggleFavorite = async (id) => {
    if (favoriteMutationData.isLoading || unfavoriteMutationData.isLoading) {
      return;
    }

    const callback = (response, state) => {
      if (!response.error) {
        setFavoriteState((prev) => ({
          ...prev,
          [id]: state,
        }));
      }
    };

    if (getFavorite(id)) {
      unfavoriteMutation({ itemId: id, itemType }).then((response) =>
        callback(response, false),
      );
    } else {
      favoriteMutation({ itemId: id, itemType }).then((response) =>
        callback(response, true),
      );
    }
  };

  return {
    getFavorite,
    toggleFavorite,
    state: favoriteState,
    isLoading:
      favoriteMutationData.isLoading || unfavoriteMutationData.isLoading,
  };
};

/**
 * @template T
 * @param {T[]} data
 * @param {number} pageSize
 * @param {unknown} [resetTrigger]
 * @returns {{
 *  slice: T[],
 *  exhausted: boolean,
 *  getNext: () => void,
 *  reset: () => void,
 * }}
 */
export const useClientPagination = (
  data,
  pageSize,
  resetTrigger = undefined,
) => {
  // TODO: if we switch this file to typescript we can do resetTrigger? as an optional argument instead of undefined
  const effectiveResetTrigger = resetTrigger ?? data;
  const [slice, setSlice] = useState(data.slice(0, pageSize));
  const page = useRef(1);
  const exhausted = slice.length >= data.length;

  const getNext = () => {
    if (exhausted) return;
    page.current += 1;
    setSlice(data.slice(0, Math.min(pageSize * page.current, data.length)));
  };

  const reset = useCallback(() => {
    page.current = 1;
    setSlice(data.slice(0, Math.min(pageSize, data.length)));
  }, [data, pageSize]);

  useEffect(() => {
    reset();
  }, [effectiveResetTrigger, pageSize]);

  useEffect(() => {
    setSlice(data.slice(0, Math.min(pageSize * page.current, data.length)));
  }, [data, pageSize]);

  return {
    slice,
    exhausted,
    getNext,
    reset,
  };
};

/**
 * @template {{[key: string]: string | number}} T
 * @param {T[]} items
 * @param {Partial<T>} filter
 * @param {{[key: keyof T]: (val: string) => string}} mapFunctions
 * @returns {T[]}
 */
const filterItems = (items, filter, mapFunctions) => {
  if (Array.isArray(items) === false) return [];
  return items.filter((item) => {
    return Object.keys(filter)
      .filter((key) => filter[key]?.toString())
      .every((key) => {
        if (typeof item[key]?.toString !== 'function') return false; // Item must be string serializable
        const mapFunction = mapFunctions[key] ?? ((value) => value); // Use provided map function or default to identity
        if (typeof mapFunction !== 'function') return false; // If a map function is provided, it must be a function
        const mappedItem = mapFunction(item[key].toString());
        const match = mappedItem
          .replace(/\s/g, '')
          .toLowerCase()
          .includes(filter[key].toString().replace(/\s/g, '').toLowerCase());
        const filterDefined = filter[key] !== undefined;
        return match || !filterDefined;
      });
  });
};

/**
 * @template T
 * @param {T[]} data
 * @param {Partial<T>} filter
 * @param {{[key: keyof T]: (val: string) => string}} mapFunctions
 * @returns {T[]}
 */
export const useClientFiltering = (data, filter, mapFunctions = {}) => {
  const [filteredData, setFilteredData] = useState(
    filterItems(data, filter, mapFunctions),
  );

  useEffect(() => {
    setFilteredData(filterItems(data, filter, mapFunctions));
  }, [data, filter, mapFunctions]);

  return filteredData;
};

/**
 *
 * @param {CallableFunction} fn
 * @param {any[]} inputs
 */
export const useUpdateEffect = (fn, inputs) => {
  const isMountingRef = useRef(false);

  useEffect(() => {
    isMountingRef.current = true;
  }, []);

  useEffect(() => {
    if (!isMountingRef.current) {
      return fn();
    } else {
      isMountingRef.current = false;
    }
  }, inputs);
};

/**
 * @return {(tsps: number[] | (current: number[]) => number[]) => void}
 */
export const useTspFilter = () => {
  const dispatch = useDispatch();
  const currentFilters = useSelector(
    /**
     *
     * @param {import('@/store').RootState} state
     * @returns
     */
    (state) => state.filters.pointAttributes?.tsp_id ?? [],
  );

  const filter = (tsps) => {
    if (typeof tsps === 'function') {
      tsps = tsps(currentFilters);
    }
    dispatch(
      filtersSlice.filtersChanged({
        filterType: 'pointAttributes',
        name: 'tsp_id',
        value: tsps,
      }),
    );
  };
  return filter;
};

/**
 *
 * @param {string} selector
 * @returns {Element | null}
 */
const _getElement = (selector) => {
  return window.document.querySelector(selector);
};

/**
 *
 * @param {string} selector
 * @returns {import('xlsx').WorkBook}
 */
const _getWorkbook = (selector) => {
  const viewElement = _getElement(selector);
  const table = viewElement?.firstElementChild;
  if (table) {
    return utils.table_to_book(table);
  }
  return utils.book_new();
};

/**
 *
 * @param {string} tableSelector
 * @param {(wb: import('xlsx').WorkBook) => void} callback
 * @param {React.DependencyList} deps
 * @returns {void}
 */
export const useWorkbook = (tableSelector, callback, deps) => {
  useEffect(() => {
    callback(_getWorkbook(tableSelector));
  }, [tableSelector, ...deps]);

  useEffect(() => {
    const tableElement = _getElement(tableSelector);
    const observer = new MutationObserver((_) => {
      callback(_getWorkbook(tableSelector));
    });
    if (tableElement) {
      observer.observe(tableElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }
    return () => {
      observer.disconnect();
    };
  }, [tableSelector, callback]);
};

/**
 *
 * @param {{
 *  baseValue?: number,
 *  zoomRange?: [number, number],
 *  a?: number,
 *  b?: number
 * }} params
 * @returns {number}
 */
export const useZoomResponsiveValue = ({
  baseValue = 1,
  zoomRange = [1, 14],
  a = -29.6752,
  b = 18.2921,
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
    },
  });

  const value = useMemo(() => {
    // I experimented with different discrete values for the scaling value
    // and did a regression analysis to find a good fit for the zoom level
    // x = zoom
    // y = value

    // Quadratic is alright, but gets weird at the extremes:
    // y = ax 2 + bx + c, where a ≠ 0
    // y = (5.5879)x 2 + (-2.4432)x + (0.2917)

    // const a = 5.5879;
    // const b = -2.4432;
    // const c = 0.2917;
    // const x = Math.min(14, Math.max(1, zoom));
    // const x2 = Math.pow(x, 2);
    // const y = Math.ceil(a * x2 + b * x + c);

    // Logarithmic is better
    // y = a + b * ln(x)
    // y = −29.6752 + 18.2921 * ln(x)
    const x = Math.min(zoomRange[1], Math.max(zoomRange[0], zoom));
    return baseValue * Math.ceil(a + b * Math.log(x));
  }, [zoom, baseValue, zoomRange, a, b]);

  return Math.max(baseValue, value);
};

export const useMapBounds = () => {
  const map = useMap();
  const [bounds, setBounds] = useState(map.getBounds());

  useMapEvents({
    moveend: () => {
      setBounds(map.getBounds());
    },
    zoomend: () => {
      setBounds(map.getBounds());
    },
  });

  return bounds;
};

export const useZoom = () => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useMapEvents({
    moveend: () => {
      setZoom(map.getZoom());
    },
    zoomend: () => {
      setZoom(map.getZoom());
    },
  });

  return zoom;
};

/**
 * @typedef {'light' | 'dark'} Theme
 */

/**
 *
 * @returns {[Theme, (theme: Theme) => void]} current theme and a function to set the theme
 */
export const useTheme = () => {
  /**
   * @type {Theme}
   */
  const _theme = localStorage.getItem('theme') || 'light';

  /**
   * @type {React.RefObject<MutationObserver>}
   */
  const observer = useRef(
    new MutationObserver((mutations) => {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-theme'
        ) {
          _setTheme(mutation.target.getAttribute('data-theme'));
        }
      });
    }),
  );

  /**
   * @type {import('../types/types').State<Theme>}
   */
  const [theme, _setTheme] = useState(
    document.body.getAttribute('data-theme') || _theme,
  );

  useEffect(() => {
    const body = document.body;
    if (body.getAttribute('data-theme') === null) {
      body.setAttribute('data-theme', _theme);
    }
  }, [document.body]);

  useEffect(() => {
    const body = document.body;

    observer.current.observe(body, {
      attributes: true,
    });

    return () => {
      observer.current.disconnect();
    };
  }, [document.body, observer]);

  /**
   * @param {Theme} theme
   * @returns {void}
   */
  const setTheme = useCallback(
    (theme) => {
      try {
        localStorage.setItem('theme', theme);
      } catch (e) {
        console.warn('Could not save theme to localStorage', e);
      }
      document.body.setAttribute('data-theme', theme);
    },
    [document.body],
  );

  return [theme, setTheme];
};
